import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getToken } from '../services/keycloak/authService';
import './MFASetupModal.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MFASetupModal = ({ isOpen, onClose, onSuccess, initialType = 'totp' }) => {
  const [step, setStep] = useState('loading'); // loading, qr, verify, email-sent, success
  const [mfaType, setMfaType] = useState(initialType); // 'totp' or 'email'
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [emailSent, setEmailSent] = useState(false);

  // Update mfaType when initialType changes
  useEffect(() => {
    setMfaType(initialType);
  }, [initialType]);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Reset states
      setStep('loading');
      setEmailSent(false);
      setError('');
      setVerificationCode('');
      // Call setup with current mfaType
      setupMFA();
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, mfaType]);

  // TOTP countdown timer (codes refresh every 30 seconds)
  useEffect(() => {
    if (step === 'qr' || step === 'verify') {
      const interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            return 30; // Reset to 30 seconds
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step]);

  const setupMFA = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      const response = await fetch(`${API_URL}/mfa/setup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: mfaType }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to setup MFA');
      }

      const data = await response.json();

      if (mfaType === 'email') {
        // For email OTP, go directly to verification step
        setStep('email-sent');
      } else {
        // For TOTP, show QR code
        setQrCode(data.qrCode);
        setSecret(data.secret);
        setManualKey(data.manualEntryKey);
        setStep('qr');
      }
    } catch (err) {
      console.error('Error setting up MFA:', err);
      setError(err.message || 'Failed to setup MFA');
      setStep(mfaType === 'email' ? 'email-sent' : 'qr');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailOTP = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      const response = await fetch(`${API_URL}/mfa/send-email-otp`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email OTP');
      }

      setEmailSent(true);
    } catch (err) {
      console.error('Error sending email OTP:', err);
      setError(err.message || 'Failed to send email OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = getToken();
      const response = await fetch(`${API_URL}/mfa/verify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: mfaType,
          secret: secret,
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Verification failed');
      }

      handleComplete();
    } catch (err) {
      console.error('Error verifying MFA:', err);
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setStep('success');
    setTimeout(() => {
      onSuccess();
      onClose();
    }, 2000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Format manual key into readable chunks (groups of 4 characters separated by space)
  const formatManualKey = (key) => {
    if (!key) return '';
    return key.match(/.{1,4}/g)?.join(' ') || key;
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="mfa-modal-overlay" onClick={handleOverlayClick}>
      <div className="mfa-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="mfa-modal-close" onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {step === 'loading' && (
          <div className="mfa-modal-step">
            <div className="mfa-modal-loading">
              <div className="spinner"></div>
              <p>Setting up two-factor authentication...</p>
            </div>
          </div>
        )}

        {step === 'qr' && (
          <div className="mfa-modal-step">
            <h2>Set Up Two-Factor Authentication</h2>
            <p className="mfa-modal-description">
              Scan the QR code below with your authenticator app (Google Authenticator, Microsoft
              Authenticator, Authy, etc.)
            </p>

            {qrCode && (
              <div className="mfa-qr-container">
                <img src={qrCode} alt="QR Code" className="mfa-qr-code" />
                <div className="mfa-timer">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>Code refreshes in {timeRemaining}s</span>
                </div>
              </div>
            )}

            <div className="mfa-manual-entry">
              <p className="mfa-manual-title">Can't scan the QR code?</p>
              <div className="mfa-manual-key">
                <code>{formatManualKey(manualKey)}</code>
                <button
                  className="btn-copy"
                  onClick={() => copyToClipboard(manualKey)}
                  title="Copy to clipboard"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              <p className="mfa-manual-instructions">
                Manually enter this code in your authenticator app
              </p>
            </div>

            <button
              className="mfa-btn-primary"
              onClick={() => setStep('verify')}
              disabled={loading}
            >
              Verify Code
            </button>

            {error && <div className="mfa-error">{error}</div>}
          </div>
        )}

        {step === 'verify' && (
          <div className="mfa-modal-step">
            <h2>Verify Your Code</h2>
            <p className="mfa-modal-description">
              Enter the 6-digit code from your authenticator app to complete setup
            </p>

            <div className="mfa-verify-form">
              <input
                type="text"
                className="mfa-code-input"
                placeholder="000000"
                maxLength="6"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setVerificationCode(value);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && verificationCode.length === 6) {
                    handleVerify();
                  }
                }}
                autoFocus
              />
            </div>

            <div className="mfa-modal-actions">
              <button
                className="mfa-btn-secondary"
                onClick={() => setStep('qr')}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="mfa-btn-primary"
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>

            {error && <div className="mfa-error">{error}</div>}
          </div>
        )}

        {step === 'email-sent' && (
          <div className="mfa-modal-step">
            <h2>Email OTP Setup</h2>
            <p className="mfa-modal-description">
              We'll send a 6-digit code to your email address whenever you sign in.
              {!emailSent && ' Click the button below to receive a test code.'}
            </p>

            {!emailSent ? (
              <>
                <button className="mfa-btn-primary" onClick={sendEmailOTP} disabled={loading}>
                  {loading ? 'Sending...' : 'Send Test Code'}
                </button>
              </>
            ) : (
              <>
                <div className="mfa-email-icon">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <p className="mfa-modal-description">
                  Check your email for the verification code and enter it below.
                </p>

                <div className="mfa-verify-form">
                  <input
                    type="text"
                    className="mfa-code-input"
                    placeholder="000000"
                    maxLength="6"
                    pattern="[0-9]*"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setVerificationCode(value);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && verificationCode.length === 6) {
                        handleVerify();
                      }
                    }}
                    autoFocus
                  />
                </div>

                <div className="mfa-modal-actions">
                  <button className="mfa-btn-secondary" onClick={sendEmailOTP} disabled={loading}>
                    Resend Code
                  </button>
                  <button
                    className="mfa-btn-primary"
                    onClick={handleVerify}
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </div>
              </>
            )}

            {error && <div className="mfa-error">{error}</div>}
          </div>
        )}

        {step === 'success' && (
          <div className="mfa-modal-step">
            <div className="mfa-success-icon">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>Two-Factor Authentication Enabled!</h2>
            <p className="mfa-modal-description">
              Your account is now protected with two-factor authentication.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MFASetupModal;
